import { Button, Flex, Text, Divider, Input } from "@chakra-ui/react";
import { useMemo, useState } from "react";
import DiscountCodesService from "../../../api/services/DiscountCodesService";
import { useAppToast } from "../../../hooks/useAppToast";

const OrderSummary = ({ tickets, data, update, t }) => {
    const isFree = !!data?.isFree;
    const quantities = data?.cart?.quantities || {};
    const toast = useAppToast();

    const [codeInput, setCodeInput] = useState("");
    const [isValidating, setIsValidating] = useState(false);

    const grossPrice = (ticket) =>
        ticket.price * (1 + ticket.vatPercentage / 100);

    const summary = useMemo(() => {
        if (isFree) {
            const qty = Math.max(0, Number(data?.cart?.quantity ?? 0));
            const name =
                tickets?.[0]?.name || t?.("ticket.standard") || "Bilet standardowy";
            return qty > 0 ? [{ name, quantity: qty, isFree: true, price: 0 }] : [];
        }
        return (tickets || [])
            .filter((tt) => (quantities[tt.id] || 0) > 0)
            .map((tt) => ({
                name: tt.name,
                quantity: quantities[tt.id],
                price: grossPrice(tt),
                isFree: false,
            }));
    }, [isFree, data?.cart?.quantity, tickets, quantities, t]);

    const rawTotalPrice = !isFree
        ? summary?.reduce((sum, item) => sum + item.quantity * item.price, 0)
        : 0;

    const discountAmount = data?.discountAmount || 0;
    const totalPrice = Math.max(0, rawTotalPrice - discountAmount);

    const eventId = data?.id ?? data?.eventId;

    const handleApplyDiscountCode = async () => {
        const code = codeInput.trim();
        if (!code || !eventId) return;

        const items = Object.entries(quantities)
            .map(([eventTicketTypeId, quantity]) => ({ eventTicketTypeId, quantity: Number(quantity) || 0 }))
            .filter((x) => x.quantity > 0);

        setIsValidating(true);
        try {
            const response = await DiscountCodesService.validate({ eventId, code, items });
            const result = response?.data;
            update?.({ discountCode: result?.code ?? code, discountAmount: result?.discountAmount ?? 0 });
            setCodeInput("");
            toast({
                title: t?.("order.discountCode.applied", "Kod rabatowy zastosowano"),
                status: "success",
                duration: 3000,
                isClosable: true,
            });
        } catch (e) {
            const message = e?.response?.data?.message || t?.("order.discountCode.invalid", "Nieprawidłowy kod rabatowy");
            toast({ title: message, status: "error", duration: 4000, isClosable: true });
        } finally {
            setIsValidating(false);
        }
    };

    const handleRemoveDiscountCode = () => {
        update?.({ discountCode: null, discountAmount: 0 });
    };

    return (
        <Flex direction="column" w="100%" maxW={{ base: "100%", md: "380px" }} gap="4">
            <Flex direction="column" bg="#F8F5F4" borderRadius="8px" p="16px" gap="32px">
                <Text fontFamily="body" fontWeight="600" fontSize="22px" mb="16px">
                    {t?.(isFree ? "cart.reservation" : "cart.order") ||
                        (isFree ? "Twoja rezerwacja" : "Twoje zamówienie")}
                </Text>

                <Flex direction="column" gap="8px">
                    {summary?.map((item, idx) => (
                        <Flex key={idx} justify="space-between">
                            <Flex direction={"row"} gap="8px">
                                <Text fontFamily="body" fontWeight="700" fontSize="18px">
                                    {item.quantity}x
                                </Text>
                                <Text>
                                    {item.name}
                                </Text>
                            </Flex>
                            <Text fontFamily="body" fontWeight="500" fontSize="18px" style={{ 'text-wrap': 'nowrap' }}>
                                {item.isFree
                                    ? t?.("cart.free") || "Bezpłatny"
                                    : `${item.price.toFixed(2)} zł`}
                            </Text>
                        </Flex>
                    ))}
                </Flex>

                {!data?.isFree && (
                    <Flex direction="column" gap="8px">
                        {data?.discountCode ? (
                            <Flex justify="space-between" align="center">
                                <Text fontSize="sm" color="#7A1F35" fontWeight="600">
                                    {t?.("order.discountCode.line_label", "Kod rabatowy")}: {data.discountCode}
                                </Text>
                                <Button variant="link" size="sm" color="#6E6064" onClick={handleRemoveDiscountCode}>
                                    {t?.("order.discountCode.remove", "Usuń")}
                                </Button>
                            </Flex>
                        ) : (
                            <Flex direction="column" gap="8px">
                                <Input
                                    size="sm"
                                    bg="white"
                                    w="100%"
                                    placeholder={t?.("order.discountCode.placeholder", "Kod rabatowy")}
                                    value={codeInput}
                                    onChange={(e) => setCodeInput(e.target.value.toUpperCase().replace(/\s+/g, ""))}
                                    onKeyDown={(e) => e.key === "Enter" && handleApplyDiscountCode()}
                                />
                                <Button
                                    size="sm"
                                    w="100%"
                                    bg="#7A1F35"
                                    color="white"
                                    _hover={{ bg: "#067A5F" }}
                                    isLoading={isValidating}
                                    isDisabled={!codeInput.trim()}
                                    onClick={handleApplyDiscountCode}
                                >
                                    {t?.("order.discountCode.apply", "Zastosuj")}
                                </Button>
                            </Flex>
                        )}
                    </Flex>
                )}

                {!data?.isFree &&
                    (<>
                        <Divider my="12px" borderColor="#DED3D4" />

                        {discountAmount > 0 && (
                            <Flex justify="space-between" color="#7A1F35" fontWeight="600">
                                <Text>{t?.("order.discountCode.discount_label", "Rabat")} ({data.discountCode}):</Text>
                                <Text style={{ 'text-wrap': 'nowrap' }}>-{discountAmount.toFixed(2)} zł</Text>
                            </Flex>
                        )}

                        <Flex justify="space-between" fontWeight="bold">
                            <Text>{t('order.form.summary')}:</Text>
                            <Text style={{ 'text-wrap': 'nowrap' }}>{totalPrice?.toFixed(2)} zł ({t('order.form.gross')})</Text>
                        </Flex>
                    </>
                    )}
            </Flex>

            {!data?.isFree && (
                <Flex
                    flexDirection={"column"}
                    alignItems={"center"}
                    bg="#F8F5F4"
                    borderRadius="8px"
                    px="16px"
                    py="32px"
                >
                    <Text mb="8px" fontWeight="600" color="#1E1517">
                        {t('order.form.payment.title', 'Bezpieczna płatność przez')}
                    </Text>
                    <Flex justify="center" align="center" py="2">
                        <a href="https://tpay.com/jak-to-dziala" target="_blank" rel="noreferrer" title="Obsługujemy płatności internetowe przez system płatności online Tpay">
                            <img src="https://tpay.com/img/banners/tpay_logo_blue.svg" style={{ border: 0, height: '40px' }} alt="Logo Tpay" title="Logo Tpay" />
                        </a>
                    </Flex>
                </Flex>
            )}
        </Flex>
    );
};

export default OrderSummary;
